import Plyr from "plyr";
// import "plyr/dist/plyr.css";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { lenis } from "./scrollLenis";

gsap.registerPlugin(ScrollTrigger);

export default class VideoPlayers {
    constructor() {
        this.playerInstances = [];
        this._playPromises = new WeakMap();
        this._timers = new WeakMap();
        this.init();
    }

    _ensurePlay(player) {
        if (player.playing) return Promise.resolve();
        const p = player.play();
        if (p && typeof p.then === "function") {
            this._playPromises.set(player, p);
            p.finally(() => this._playPromises.delete(player)).catch(() => {});
            return p.catch(() => {});
        }
        return Promise.resolve();
    }

    async _ensurePause(player) {
        const pending = this._playPromises.get(player);
        if (pending) {
            try { await pending; } catch (_) {}
        }
        if (player.playing) player.pause();
    }

    _debouncePer(player, fn, delay = 100) {
        clearTimeout(this._timers.get(player));
        const t = setTimeout(fn, delay);
        this._timers.set(player, t);
    }

    pauseOtherPlayers(currentPlayer) {
        this.playerInstances.forEach((instance) => {
            if (instance !== currentPlayer && instance.playing) {
                this._ensurePause(instance);
            }
        });
    }

    pauseAllPlayers() {
        this.playerInstances.forEach((instance) => this._ensurePause(instance));
    }

    get defaultOptions() {
        return {
            fullscreen: { enabled: true, iosNative: false },
            controls: ["play-large", "play", "progress", "current-time", "mute", "volume", "fullscreen"],
            autoplay: false,
            storage: { enabled: false },
            youtube: { noCookie: true, rel: 0, modestbranding: 1, showinfo: 0 },
            vimeo: { byline: 0, portrait: 0, title: 0, dnt: 1, transparent: 0, speed: 1, controls: false },
        };
    }

    init() {
        const players = Array.from(document.querySelectorAll(".plyr-player"));
        players.forEach((player) => this.initSinglePlayer(player));
        gsap.delayedCall(0.2, () => ScrollTrigger.refresh());
    }

    initSinglePlayer(player, forceAutoplay = false) {
        let playerInstance = null;

        const iframe = player.querySelector("iframe");
        const video = player.querySelector("video.plyr");
        const isAutoplay = forceAutoplay || player.closest(".autoplay") !== null;

        const options = { ...this.defaultOptions, muted: isAutoplay };
        let targetEl = null;

        // ==== IFRAME (YouTube / Vimeo) ====
        if (iframe) {
            const allow = iframe.getAttribute("allow") || "";
            if (!/autoplay/.test(allow)) {
                iframe.setAttribute(
                    "allow",
                    `${allow ? allow + "; " : ""}autoplay; encrypted-media; picture-in-picture; fullscreen`
                );
            }

            let src = iframe.getAttribute("src") || "";

            // --- YouTube
            if (src.includes("youtube.com/embed/")) {
                const cleanSrc = src.split("?")[0];
                const params = new URLSearchParams({
                    autoplay: isAutoplay ? 1 : 0,
                    mute: isAutoplay ? 1 : 0,
                    controls: 1,
                    modestbranding: 1,
                    rel: 0,
                    showinfo: 0,
                    enablejsapi: 1,
                    origin: window.location.origin,
                });
                iframe.setAttribute("src", `${cleanSrc}?${params.toString()}`);
                targetEl = iframe;
            }

            // --- Vimeo
            if (src.includes('player.vimeo.com/video/') || src.includes('vimeo.com/')) {
                let vimeoId = null;
                const m1 = src.match(/player\.vimeo\.com\/video\/(\d+)/);
                const m2 = src.match(/vimeo\.com\/(\d+)/);
                if (m1) vimeoId = m1[1];
                else if (m2) vimeoId = m2[1];

                if (vimeoId) {
                    const provider = document.createElement('div');
                    provider.className = 'plyr';
                    provider.setAttribute('data-plyr-provider', 'vimeo');
                    provider.setAttribute('data-plyr-embed-id', vimeoId);
                    provider.setAttribute('data-plyr-config', JSON.stringify({
                        vimeo: { controls: false, byline: 0, title: 0, portrait: 0, dnt: 1, transparent: 0, speed: 1 }
                    }));

                    iframe.replaceWith(provider);
                    targetEl = provider;
                }
            }
        }

        // ==== <video> ====
        if (video) {
            video.setAttribute("playsinline", "");
            if (isAutoplay) {
                video.muted = true;
                video.setAttribute("muted", "");
            }
            targetEl = video;
        }

        if (!targetEl) {
            targetEl = player.querySelector(".plyr") || video || iframe;
        }

        // ==== Instance Plyr ====
        if (targetEl) {
            playerInstance = new Plyr(targetEl, options);
            player.plyr = playerInstance;

            playerInstance.on("play", () => this.pauseOtherPlayers(playerInstance));

            // --- FULLSCREEN × LENIS ---
            let savedScroll = null;

            const restoreScrollSoon = () => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (savedScroll != null) {
                            lenis?.scrollTo?.(savedScroll, { immediate: true });
                            savedScroll = null;
                            ScrollTrigger.refresh();
                        }
                    });
                });
            };

            const onEnterFS = () => {
                savedScroll = (lenis?.scroll ?? window.scrollY);
                lenis?.stop?.();
            };

            const onExitFS = () => {
                lenis?.start?.();
                restoreScrollSoon();
            };

            playerInstance.on("enterfullscreen", onEnterFS);
            playerInstance.on("exitfullscreen", onExitFS);

            const fsHandler = () => {
                if (!document.fullscreenElement) {
                    onExitFS();
                }
            };
            document.addEventListener("fullscreenchange", fsHandler);

            playerInstance.on("destroyed", () => {
                document.removeEventListener("fullscreenchange", fsHandler);
            });

            const attachScrollTrigger = () => {
                ScrollTrigger.create({
                    trigger: player,
                    start: "top 80%",
                    end: "bottom 25%",
                    onEnter: () => {
                        this._debouncePer(playerInstance, async () => {
                            if (isAutoplay) await this._ensurePlay(playerInstance);
                            this.pauseOtherPlayers(playerInstance);
                        });
                    },
                    onEnterBack: () => {
                        this._debouncePer(playerInstance, async () => {
                            if (isAutoplay) await this._ensurePlay(playerInstance);
                            this.pauseOtherPlayers(playerInstance);
                        });
                    },
                    onLeave: () => this._debouncePer(playerInstance, () => this._ensurePause(playerInstance)),
                    onLeaveBack: () => this._debouncePer(playerInstance, () => this._ensurePause(playerInstance)),
                });
            };

            if (targetEl.tagName !== "VIDEO") {
                playerInstance.once("ready", () => {
                    if (isAutoplay) playerInstance.muted = true;
                    attachScrollTrigger();
                    if (forceAutoplay) this._ensurePlay(playerInstance);
                });
            } else {
                attachScrollTrigger();
            }

            this.playerInstances.push(playerInstance);
        }

        return playerInstance;
    }
}
